import { useCallback, useEffect, useMemo, useState } from "react";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import { SectionCard } from "@/components/common/SectionCard";
import {
  canSubmitServiceRequestWithPhotos,
  ServiceRequestPhotoUpload,
} from "@/components/service/ServiceRequestPhotoUpload";
import { createServiceRequest, listServiceOfferings } from "@/lib/service/client";
import type { ServiceRequestAttachment } from "@/lib/service/requestAttachmentsClient";
import {
  originForJobKind,
  parseOpsDealId,
  parseQuotedAmountCents,
  type ServiceJobKind,
} from "@/lib/service/serviceJobKind";
import {
  serviceRequestDescriptionPlaceholder,
  serviceRequestIntakeHelpText,
  serviceRequestPhotosHelpText,
} from "@/lib/service/serviceRequestIntakeCopy";
import type { ServiceOffering, ServiceWorkQueue } from "@/lib/service/types";
import { ghostBtnClass, inputClass, primaryBtnClass } from "./serviceShared";
import { ServiceOriginalDealField } from "./ServiceOriginalDealField";
import type { OpsDealPick } from "@/lib/service/opsDealPicker";

type Props = {
  queues: ServiceWorkQueue[];
  onSubmitted: () => void;
  /** Coordinator path: create + approve so the job is unscheduled on the board. */
  placeOnBoard?: boolean;
};

function newRequestId(): string {
  return crypto.randomUUID();
}

export function ServiceRequestManualEntryPanel({ queues, onSubmitted, placeOnBoard }: Props) {
  const [offerings, setOfferings] = useState<ServiceOffering[]>([]);
  const [requestId, setRequestId] = useState(() => newRequestId());
  const [jobKind, setJobKind] = useState<ServiceJobKind>("sold");
  const [opsDeal, setOpsDeal] = useState<OpsDealPick | null>(null);
  const [quotedUsd, setQuotedUsd] = useState("");
  const [queueId, setQueueId] = useState("");
  const [offeringId, setOfferingId] = useState("");
  const [locationLabel, setLocationLabel] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [description, setDescription] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [attachments, setAttachments] = useState<ServiceRequestAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedOffering = useMemo(
    () => offerings.find((o) => o.id === offeringId) ?? null,
    [offerings, offeringId]
  );
  const selectedQueue = useMemo(
    () => queues.find((q) => q.id === queueId) ?? null,
    [queues, queueId]
  );
  const photosRequired = selectedQueue?.photos_required === true;
  const canSubmit = canSubmitServiceRequestWithPhotos(photosRequired, attachments.length);

  useEffect(() => {
    void (async () => {
      const res = await listServiceOfferings({ active_only: true });
      if (res.ok) setOfferings(res.data.offerings);
    })();
  }, []);

  const handleOfferingChange = useCallback(
    (nextOfferingId: string) => {
      setOfferingId(nextOfferingId);
      if (!nextOfferingId) return;
      const offering = offerings.find((o) => o.id === nextOfferingId);
      if (offering?.default_queue_id) {
        setQueueId(offering.default_queue_id);
      }
    },
    [offerings]
  );

  const resetFields = useCallback(() => {
    setRequestId(newRequestId());
    setJobKind("sold");
    setOpsDeal(null);
    setQuotedUsd("");
    setQueueId("");
    setOfferingId("");
    setLocationLabel("");
    setLocationAddress("");
    setDescription("");
    setTimeframe("");
    setAttachments([]);
  }, []);

  const resetForm = useCallback(() => {
    resetFields();
    setError(null);
    setSuccess(null);
  }, [resetFields]);

  const submit = useCallback(async () => {
    if (!description.trim() && !locationLabel.trim()) {
      setError("Add a site label or operational description before submitting.");
      return;
    }
    if (!canSubmit) {
      setError("Add at least one site photo for this job type before submitting.");
      return;
    }
    if (jobKind === "internal" && opsDeal && !parseOpsDealId(opsDeal.id)) {
      setError("Pick an original deal from the list, or leave it blank.");
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    const res = await createServiceRequest({
      id: requestId,
      origin: originForJobKind(jobKind),
      quoted_amount_cents: jobKind === "sold" ? parseQuotedAmountCents(quotedUsd) : null,
      job_kind: jobKind,
      ops_deal_id: jobKind === "internal" ? parseOpsDealId(opsDeal?.id) : null,
      queue_id: queueId || null,
      offering_id: offeringId || null,
      description: description.trim(),
      preferred_timeframe: timeframe.trim(),
      location_label: locationLabel.trim(),
      location_address: locationAddress.trim(),
      dispatch_now: placeOnBoard === true,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSuccess(
      placeOnBoard
        ? "Job is on the board as unscheduled. Assign a rig and date below."
        : "Request submitted — it will appear in the pending queue on the left."
    );
    onSubmitted();
    resetFields();
  }, [
    canSubmit,
    description,
    locationLabel,
    locationAddress,
    timeframe,
    queueId,
    offeringId,
    requestId,
    quotedUsd,
    jobKind,
    opsDeal,
    onSubmitted,
    resetFields,
    placeOnBoard,
  ]);

  const form = (
    <>
      <p className="mb-3 text-xs text-gray-600 dark:text-zinc-400">
        {placeOnBoard
          ? "Opens a job on this board now (unscheduled). Hang customer-facing warranty on the follow-on deal for that serial — not the original sale. Internal/mfg-auto jobs may stay on the original deal. Sold mints its own deal."
          : "Submit a request through the same intake pipeline as Sales. Customer-facing warranty is a follow-on deal on the same serial. Internal/mfg-auto stays a job without a new customer deal. Sold is a new service sale and gets its own deal when approved."}
      </p>

      <div className="space-y-3">
        <select
          className={inputClass}
          value={jobKind}
          onChange={(e) => setJobKind(e.target.value === "sold" ? "sold" : "internal")}
        >
          <option value="internal">Internal — warranty / repair (link the follow-on deal on this serial)</option>
          <option value="sold">Sold — new service sale (own deal on approve)</option>
        </select>
        {jobKind === "internal" ? (
          <ServiceOriginalDealField
            selected={opsDeal}
            disabled={busy}
            onPick={setOpsDeal}
            onClear={() => setOpsDeal(null)}
          />
        ) : (
          <input
            className={inputClass}
            type="number"
            min={0}
            step="0.01"
            placeholder="Quoted amount (USD, optional)"
            value={quotedUsd}
            onChange={(e) => setQuotedUsd(e.target.value)}
          />
        )}
        <select
          className={inputClass}
          value={offeringId}
          onChange={(e) => handleOfferingChange(e.target.value)}
        >
          <option value="">Service offering (optional)</option>
          {offerings.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        {offerings.length === 0 ? (
          <p className="text-xs text-amber-800 dark:text-amber-200">
            No offerings yet. Author them in Service setup (generic names, not a partner brand).
          </p>
        ) : null}
        {selectedOffering?.default_queue_id ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            Job type autofills from {selectedOffering.label}; you can change it below.
          </p>
        ) : null}
        <select className={inputClass} value={queueId} onChange={(e) => setQueueId(e.target.value)}>
          <option value="">Job type (optional)</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>
              {q.label}
              {q.photos_required ? " · photos required" : ""}
            </option>
          ))}
        </select>
        {queues.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            No job types yet — the job still lands on the board under No job type.
          </p>
        ) : null}
        <input
          className={inputClass}
          placeholder="Site / customer label"
          value={locationLabel}
          onChange={(e) => setLocationLabel(e.target.value)}
        />
        <AddressAutocompleteInput
          value={locationAddress}
          onChange={setLocationAddress}
          placeholder="Site address — start typing to search"
          disabled={busy}
          className={inputClass}
          onPick={(pick) => {
            if (!locationLabel.trim()) {
              const label =
                pick.normalized.street?.trim() ||
                pick.label.split(",")[0]?.trim() ||
                "";
              if (label) setLocationLabel(label);
            }
          }}
        />
        <textarea
          className={`${inputClass} min-h-[80px]`}
          placeholder={serviceRequestDescriptionPlaceholder(selectedQueue)}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          {serviceRequestIntakeHelpText(selectedQueue)}
        </p>
        <input
          className={inputClass}
          placeholder="Preferred timeframe (optional)"
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
        />
        <div>
          {serviceRequestPhotosHelpText(selectedQueue) ? (
            <p className="mb-1 text-xs text-amber-800 dark:text-amber-200">
              {serviceRequestPhotosHelpText(selectedQueue)}
            </p>
          ) : null}
          <ServiceRequestPhotoUpload
            requestId={requestId}
            required={photosRequired}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            disabled={busy}
          />
        </div>
      </div>

      {error ? <p className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {success ? (
        <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">{success}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button type="button" className={ghostBtnClass} onClick={resetForm} disabled={busy}>
          Clear
        </button>
        <button
          type="button"
          className={primaryBtnClass}
          onClick={() => void submit()}
          disabled={busy || !canSubmit}
        >
          {busy ? "Submitting…" : placeOnBoard ? "Add to board" : "Submit request"}
        </button>
      </div>
    </>
  );

  if (placeOnBoard) return form;
  return <SectionCard title="Manual entry">{form}</SectionCard>;
}
